/* eslint-disable max-lines */
import type { Space } from '@mail/cross-sizes-utils';
import { Icon16CopyOutline, Icon16InfoOutline, Icon16PenOutline, Icon16Stars } from '@vkontakte/icons';
import theme from '@vkontakte/vkui-tokens/themes/cloud/cssVars/theme';
import { ReactComponent as AdDisableIcon } from 'img/icons/adDisableIcon16.svg';
import { ReactComponent as CancelIcon } from 'img/icons/cancel_benefit.svg';
import { ReactComponent as DiskoIcon } from 'img/icons/disko.svg';
import { ReactComponent as DoneIcon } from 'img/icons/done_benefit.svg';
import { ReactComponent as VkMusicIcon } from 'img/icons/vk_music.svg';
import { plural } from 'lib/plural';
import React from 'react';
import { isFamilySubsInTariff, isOnlyWebAdInTariffs, isRebranding } from 'reactApp/appHelpers/featuresHelpers';
import {
    showEditingDocumentFeatureB,
    showEditingDocumentFeatureC,
} from 'reactApp/appHelpers/featuresHelpers/features/showEditingDocumentFeature';
import { PaymentUTM } from 'reactApp/constants/paymentUTM';
import type { IFeatureData } from 'reactApp/constants/products/features.types';
import { templateFeatureActions } from 'reactApp/constants/products/templateFeatureActions';
import { templateFeatureIcons } from 'reactApp/constants/products/templateFeatureIcons';
import { templateFeatures } from 'reactApp/constants/products/templateFeatures';
import { SPACE_LIST } from 'reactApp/constants/spaceList';
import { sendDwhRadar } from 'reactApp/modules/dwh/dwh.helpers';
import { renderAiTariffPromoDialog } from 'reactApp/sections/QuotaLanding/aiTariffPromoDialog/aiTariffPromo.helpers';
import { getQuotaPromoUrl } from 'reactApp/sections/QuotaLanding/QuotaLanding.data';
import type { Product } from 'reactApp/types/Billing';

import { benefitsBySpace } from './benefits.bySpace';
import { type BenefitInfoType, BenefitKeyType, benefitsInfo } from './benefits.info';
import styles from './quotaFeatures.css';

const withoutAd = isOnlyWebAdInTariffs ? templateFeatures.noadsWeb : templateFeatures.noadsMailCloud;
const { vkui12Cancel, vkui16Picture, vkui16Pen, vkui16Cloud } = templateFeatureIcons;

const baseQuotaFeatures: IFeatureData[] = [
    {
        ...withoutAd,
        textJsx: !isOnlyWebAdInTariffs && (
            <>
                <a target="_blank" href={getQuotaPromoUrl({ query: PaymentUTM.featuresTariffsCard })} rel="noreferrer">
                    Без рекламы
                </a>
                {'\u00A0'}в Почте и Облаке
            </>
        ),
    },
    templateFeatures.upload100Cloud,
    templateFeatures.send100Mail,
    ...(isRebranding
        ? [templateFeatures.familyShare, templateFeatures.supportPriority]
        : [templateFeatures.foldersAutodelete, isFamilySubsInTariff ? templateFeatures.familyShare : templateFeatures.supportPriority]),
];
const baseQuotaProFeatures: IFeatureData[] = [
    {
        text: 'Лицензия Диск-О: на год в подарок',
        key: 'disko',
        icon: <DiskoIcon width={16} height={16} />,
        onClick: templateFeatureActions.openDisko,
        delimiters: true,
    },
    {
        text: 'VK Музыка бесплатно на 3\u00A0месяца',
        key: 'combo',
        icon: <VkMusicIcon width={16} height={16} viewBox="0 0 18 18" />,
        delimiters: true,
    },
];

const baseQuotaProFeatures_6m = [
    {
        ...baseQuotaProFeatures[0],
        text: 'Лицензия ДискО: на 6\u00A0месяцев в подарок',
    },
    {
        ...baseQuotaProFeatures[1],
        text: 'VK Музыка бесплатно на 1\u00A0месяц',
    },
];

const baseQuotaCompact: IFeatureData[] = [
    withoutAd,
    isFamilySubsInTariff ? templateFeatures.familyShare : templateFeatures.supportFastAnswer,
    templateFeatures.duplicates,
    templateFeatures.otherBenefits,
];

export const getQuotaCompactFeatures = (space: Space) => {
    const features = baseQuotaCompact.map((item) => {
        if (item.key === templateFeatures.duplicates.key) {
            return {
                ...item,
                disabled: space.original < SPACE_LIST.gb_256,
            };
        }
        return item;
    });

    if (showEditingDocumentFeatureB || showEditingDocumentFeatureC) {
        return [
            {
                ...templateFeatures.editingDocuments,
                disabled: space.original < SPACE_LIST.gb_512,
            },
            ...features,
        ];
    }
    return features;
};

const baseFreeQuotaCompact: IFeatureData[] = [
    {
        ...withoutAd,
        icon: vkui12Cancel,
    },
    {
        text: 'Нет загрузки больших файлов',
        key: 'upload',
        icon: vkui12Cancel,
    },
    isFamilySubsInTariff
        ? {
              text: 'Делиться местом нельзя',
              key: 'family',
              icon: vkui12Cancel,
          }
        : {
              text: 'Нет быстрой поддержки',
              key: 'support',
              icon: vkui12Cancel,
          },
    {
        text: 'Нет дополнительных возможностей',
        textJsx: (
            <>
                Нет{'\u00A0'}дополнительных
                <br />
                <a href="#benefits">возможностей</a>
            </>
        ),
        key: 'benefits',
        icon: vkui12Cancel,
    },
];

const singleQuotaCheapest: IFeatureData[] = [
    withoutAd,
    {
        text: 'Нет загрузки больших файлов',
        key: 'upload',
        icon: vkui12Cancel,
    },
    isFamilySubsInTariff
        ? {
              text: 'Делиться местом нельзя',
              key: 'family',
              icon: vkui12Cancel,
          }
        : {
              text: 'Нет быстрой поддержки',
              key: 'support',
              icon: vkui12Cancel,
          },
    {
        text: 'Нет дополнительных возможностей',
        textJsx: (
            <>
                Нет{'\u00A0'}дополнительных
                <br />
                <a href="#benefits">возможностей</a>
            </>
        ),
        key: 'benefits',
        icon: vkui12Cancel,
    },
];

export const multiTariffBasic: IFeatureData[] = [
    {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        text: 'Безлимит для фото с телефона',
        // eslint-disable-next-line sonarjs/no-duplicate-string
        key: 'unlimit-autoupload',
    },
    {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        text: 'Удаление дубликатов',
        key: 'duplicates',
        icon: vkui12Cancel,
    },
    {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        text: 'Добавление ещё 3 участников',
        key: 'family',
        icon: vkui12Cancel,
    },
    {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        text: 'Без рекламы в Почте и Облаке',
        key: 'ad',
        icon: vkui12Cancel,
    },
    {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        text: 'И другие возможности',
        textJsx: (
            <>
                И другие <a href="#benefits">возможности</a>
            </>
        ),
        key: 'benefits',
        icon: vkui12Cancel,
    },
];

export const familyByOptionsBasic: IFeatureData[] = [
    {
        text: 'Безлимит для фото с телефона',
        key: 'unlimit-autoupload',
    },
    {
        text: 'Удаление дубликатов',
        key: 'duplicates',
        icon: vkui12Cancel,
    },
    {
        text: 'Без рекламы в Почте и Облаке',
        key: 'ad',
        icon: vkui12Cancel,
    },
    {
        text: 'И другие возможности',
        textJsx: (
            <>
                И другие <a href="#benefits">возможности</a>
            </>
        ),
        key: 'benefits',
        icon: vkui12Cancel,
    },
];

export const familyByOptionsAdvanced: IFeatureData[] = [
    {
        text: 'Безлимит для фото с телефона',
        key: 'unlimit-autoupload',
    },
    {
        text: 'Удаление дубликатов',
        key: 'duplicates',
        primaryColor: true,
    },
    {
        text: 'Без рекламы в Почте и Облаке',
        key: 'ad',
        primaryColor: true,
    },
    {
        text: 'И другие возможности',
        textJsx: (
            <>
                И другие <a href="#benefits">возможности</a>
            </>
        ),
        key: 'benefits',
        primaryColor: true,
    },
];

export const multiTariffAdvanced: IFeatureData[] = [
    {
        text: 'Безлимит для фото с телефона',
        key: 'unlimit-autoupload',
    },
    {
        text: 'Удаление дубликатов',
        key: 'duplicates',
        primaryColor: true,
    },
    {
        text: 'Добавление ещё 3 участников',
        key: 'family',
        primaryColor: true,
    },
    {
        text: 'Без рекламы в Почте и Облаке',
        key: 'ad',
        primaryColor: true,
    },
    {
        text: 'И другие возможности',
        textJsx: (
            <>
                И другие <a href="#benefits">возможности</a>
            </>
        ),
        key: 'benefits',
        primaryColor: true,
    },
];

export const multiTariffVibgo: IFeatureData[] = [
    {
        text: 'Безлимит для фото с телефона',
        key: 'unlimit-autoupload',
        primaryColor: true,
    },
    {
        text: 'Создание галерей Vigbo',
        key: 'vigbo-create',
        primaryColor: true,
        icon: vkui16Picture,
        usePropsIcon: true,
    },
    {
        text: 'Функция ретуши в Vigbo',
        key: 'vigbo-edit',
        primaryColor: true,
        icon: vkui16Pen,
        usePropsIcon: true,
    },
    {
        text: 'Загрузка файлов до 100 ГБ',
        key: 'upload',
        primaryColor: true,
        icon: vkui16Cloud,
        usePropsIcon: true,
    },
    {
        text: 'И другие преимущества',
        textJsx: (
            <>
                И другие <a href="#benefits">преимущества</a>
            </>
        ),
        key: 'benefits',
        primaryColor: true,
    },
];

export const multiTariffFamily: IFeatureData[] = [
    {
        text: 'Добавление ещё 3 участников',
        key: 'family-tariff',
        primaryColor: true,
    },
    {
        text: 'Семейные папки и альбомы',
        key: 'family-folders-albums',
        primaryColor: true,
    },
    {
        text: 'Без рекламы в Почте и Облаке',
        key: 'family-disable-ad',
        primaryColor: true,
        usePropsIcon: true,
        icon: <AdDisableIcon />,
    },
    {
        text: 'Безлимит у всей семьи',
        key: 'unlimit-autoupload',
        primaryColor: true,
    },
    {
        text: 'И другие возможности',
        textJsx: (
            <>
                И другие <a href="#benefits">преимущества</a>
            </>
        ),
        key: 'benefits',
        primaryColor: true,
    },
];

/* tempexp_17509-start */
const litresFeatures: IFeatureData[] = [
    {
        text: 'Подписка на Литрес — 2 месяца бесплатно',
        textJsx: (
            <>
                Подписка на Литрес — 2 месяца
                <br />
                бесплатно
            </>
        ),
        key: 'litres',
    },
    withoutAd,
    isFamilySubsInTariff ? templateFeatures.familyAdd3 : templateFeatures.supportFastAnswer,
    templateFeatures.otherBenefits,
];
/* tempexp_17509-end */

// tempexp_19645-start
const aiTariffFeatures: IFeatureData[] = [
    {
        text: 'Безлимит для фото с телефона',
        key: 'unlimit-autoupload',
        primaryColor: true,
    },
    {
        text: 'Удаление дубликатов',
        key: 'duplicates',
        icon: <Icon16CopyOutline />,
        primaryColor: true,
        usePropsIcon: true,
    },
    {
        text: 'Генерация и изменение писем',
        key: 'generate_ai',
        icon: <Icon16PenOutline />,
        primaryColor: true,
        usePropsIcon: true,
    },
    {
        text: 'Нейропомощник в Почте',
        key: 'neuroassistant',
        icon: <Icon16Stars />,
        primaryColor: true,
        usePropsIcon: true,
    },
    {
        text: 'И другие возможности',
        textJsx: (
            <>
                И другие <a href="#benefits">возможности</a>
            </>
        ),
        key: 'benefits',
        primaryColor: true,
    },
];

const aiTariffFeaturesVariantD: IFeatureData[] = [
    {
        text: 'Безлимит для фото с телефона',
        key: 'unlimit-autoupload',
        primaryColor: true,
    },
    {
        text: 'Функции нейросети',
        key: 'neuroassistant',
        icon: <Icon16Stars />,
        primaryColor: true,
        usePropsIcon: true,
        onClick: () => {
            sendDwhRadar({ t: 'union-quota', action: 'icon-info', name: 'mnz_neiro_functions' });
            renderAiTariffPromoDialog({});
        },
        textJsx: (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                Функции нейросети{' '}
                <span className={styles.infoByHover} data-info-by-hover={true}>
                    <Icon16InfoOutline
                        className={styles.infoByHover}
                        fill={theme.colorIconAccent.normal.value}
                        width={16}
                        height={16}
                        onClick={() => {}}
                    />
                </span>
            </div>
        ),
    },
    {
        text: 'Удаление дубликатов',
        key: 'duplicates',
        primaryColor: true,
    },
    {
        text: 'Без рекламы в Почте и Облаке',
        key: 'ad',
        primaryColor: true,
    },
    {
        text: 'И другие возможности',
        textJsx: (
            <>
                И другие <a href="#benefits">возможности</a>
            </>
        ),
        key: 'benefits',
        primaryColor: true,
    },
];

const aiTariffFeaturesBasicVariantD: IFeatureData[] = aiTariffFeaturesVariantD.map((item, idx) =>
    idx === 0
        ? item
        : {
              ...item,
              primaryColor: false,
              icon: vkui12Cancel,
              usePropsIcon: false,
          }
);
// tempexp_19645-end

export const quotaFeatures: Record<string, IFeatureData[]> = {
    quotaTariffsLanding: baseQuotaFeatures,
    freeQuotaCompact: baseFreeQuotaCompact,
    quotaRegular: baseQuotaFeatures,
    quotaCompact: baseQuotaCompact,
    quotaProfessional: [...baseQuotaFeatures, ...baseQuotaProFeatures],
    quotaProfessional_6m: [...baseQuotaFeatures, ...baseQuotaProFeatures_6m],
    singleQuotaCheapest,
    litresFeatures,
    multiTariffBasic,
    multiTariffAdvanced,
    familyByOptionsBasic,
    familyByOptionsAdvanced,
    aiTariffFeatures,
    aiTariffFeaturesVariantD,
    aiTariffFeaturesBasicVariantD,
    multiTariffVibgo,
    multiTariffFamily,
};

// tempexp_16800-start
const featureIds = [benefitsInfo[BenefitKeyType.Ad].key, benefitsInfo[BenefitKeyType.Upload].key, benefitsInfo[BenefitKeyType.Family].key];
const expandedFeatureIds = [
    ...featureIds,
    benefitsInfo[BenefitKeyType.Support].key,
    benefitsInfo[BenefitKeyType.Calendar].key,
    benefitsInfo[BenefitKeyType.Safety].key,
    benefitsInfo[BenefitKeyType.Mobile].key,
    benefitsInfo[BenefitKeyType.Folders].key,
];

const quotaFeaturesBySpace: Record<string, IFeatureData[]> = {};

export function createQuotaFeaturesListByProductSpace(product: Product, variant: 'all' | 'all-active', shortText: boolean) {
    const cacheKey = `${product.space.original}-${variant}-${shortText ? 'short' : 'key'}`;
    const result: IFeatureData[] = quotaFeaturesBySpace[cacheKey] ?? [];

    if (product.isSingleQuota && !result.length) {
        expandedFeatureIds.forEach((featureKey) => {
            const benefit: BenefitInfoType | undefined = benefitsBySpace[product.space.original]?.[featureKey];
            if (benefit && variant === 'all-active') {
                result.push({
                    key: featureKey,
                    text: shortText ? benefit.shortText : benefit.fullText,
                });
            }
            if (variant === 'all') {
                if (benefit) {
                    const text = shortText ? benefit.shortText : benefit.fullText;
                    result.push({
                        key: featureKey,
                        text,
                        icon: <DoneIcon />,
                    });
                } else {
                    const text = shortText ? benefitsInfo[featureKey].shortText : benefitsInfo[featureKey].fullText;
                    result.push({
                        key: featureKey,
                        text,
                        textJsx: <span className={styles.cancelText}>{text}</span>,
                        icon: <CancelIcon />,
                    });
                }
            }
        });
        quotaFeaturesBySpace[cacheKey] = result;
    }
    return result;
}

export function getAdditionalBenefitsCountText(product: Product) {
    const pluralTitles = ['возможность', 'возможности', 'возможностей'];
    const activeBenefits = Object.values(benefitsBySpace[product.space.original] ?? {}).filter((b) => !!b);
    const count = activeBenefits.length - featureIds.length;
    return `${count} ${pluralTitles[plural(count)]}`;
    // eslint-disable-next-line max-lines
}

// tempexp_16800-end
